import { createDemoStore } from '@/demo/core/create-demo-store';
import { getSop } from './agents-sops';
import { getAgent } from './agents-data';
import {
  AUTONOMY_LABEL,
  STATUS_LABEL,
  guardrailSentence,
  parseSopText,
} from './agents-logic';
import type { ConfigEvent } from './agents-logic';
import type {
  AgentDef,
  AgentId,
  AgentStatus,
  AutonomyLevel,
  Guardrail,
  SopStep,
} from './agents-types';

export interface GuardrailState {
  enabled: boolean;
  value: number;
}

export interface SopOverride {
  steps: SopStep[];
  version: number;
  editedAtMs: number;
}

export interface ReplayRun {
  completedAtMs: number;
}

/**
 * Only what the viewer changed is stored; everything else reads from the seed
 * data, so a reset is just an empty state.
 */
export interface AgentsState {
  autonomy: Partial<Record<AgentId, AutonomyLevel>>;
  status: Partial<Record<AgentId, AgentStatus>>;
  guardrails: Record<string, GuardrailState | undefined>;
  sops: Partial<Record<AgentId, SopOverride>>;
  replays: Partial<Record<AgentId, ReplayRun>>;
  events: ConfigEvent[];
}

function emptyState(): AgentsState {
  return {
    autonomy: {},
    status: {},
    guardrails: {},
    sops: {},
    replays: {},
    events: [],
  };
}

export const agentsStore = createDemoStore(emptyState);

export function resetAgentsDemo(): void {
  agentsStore.reset();
}

let eventCounter = 0;

function pushEvent(
  draft: AgentsState,
  agentId: AgentId,
  action: string,
  detail: string,
): void {
  eventCounter += 1;
  draft.events.unshift({
    id: `config-${eventCounter}`,
    agentId,
    atMs: Date.now(),
    action,
    detail,
  });
}

export function defaultGuardrailState(guardrail: Guardrail): GuardrailState {
  const { control } = guardrail;
  if (control.type === 'limit') {
    return { enabled: true, value: control.value };
  }
  if (control.type === 'rule') {
    return { enabled: control.enabled, value: 0 };
  }
  return { enabled: true, value: 0 };
}

export function setAutonomy(agent: AgentDef, level: AutonomyLevel): void {
  agentsStore.update((draft) => {
    const previous = draft.autonomy[agent.id] ?? agent.defaultAutonomy;
    if (previous === level) return;
    draft.autonomy[agent.id] = level;
    pushEvent(
      draft,
      agent.id,
      'Changed autonomy level',
      `${AUTONOMY_LABEL[previous]} to ${AUTONOMY_LABEL[level]}`,
    );
  });
}

export function setStatus(agent: AgentDef, status: AgentStatus): void {
  agentsStore.update((draft) => {
    const previous = draft.status[agent.id] ?? agent.defaultStatus;
    if (previous === status) return;
    draft.status[agent.id] = status;
    pushEvent(
      draft,
      agent.id,
      'Changed agent status',
      `${STATUS_LABEL[previous]} to ${STATUS_LABEL[status]}`,
    );
  });
}

export function setGuardrailEnabled(
  agent: AgentDef,
  guardrail: Guardrail,
  enabled: boolean,
): void {
  agentsStore.update((draft) => {
    const current =
      draft.guardrails[guardrail.id] ?? defaultGuardrailState(guardrail);
    if (current.enabled === enabled) return;
    draft.guardrails[guardrail.id] = { ...current, enabled };
    pushEvent(
      draft,
      agent.id,
      enabled ? 'Turned on guardrail' : 'Turned off guardrail',
      guardrailSentence(guardrail, current.value),
    );
  });
}

export function setGuardrailValue(
  agent: AgentDef,
  guardrail: Guardrail,
  value: number,
): void {
  agentsStore.update((draft) => {
    const current =
      draft.guardrails[guardrail.id] ?? defaultGuardrailState(guardrail);
    if (current.value === value) return;
    draft.guardrails[guardrail.id] = { ...current, value };
    pushEvent(
      draft,
      agent.id,
      'Changed guardrail limit',
      guardrailSentence(guardrail, value),
    );
  });
}

export function saveSop(agentId: AgentId, text: string): boolean {
  const base = getSop(agentId);
  const current = agentsStore.getState().sops[agentId];
  const steps = parseSopText(text, current?.steps ?? base.steps);
  if (steps.length === 0) return false;
  const version = (current?.version ?? base.version) + 1;
  agentsStore.update((draft) => {
    draft.sops[agentId] = { steps, version, editedAtMs: Date.now() };
    pushEvent(
      draft,
      agentId,
      'Updated agent instructions',
      `${base.id} v${version - 1} to v${version}, ${steps.length} steps`,
    );
  });
  return true;
}

export function completeReplay(agentId: AgentId): void {
  agentsStore.update((draft) => {
    draft.replays[agentId] = { completedAtMs: Date.now() };
    pushEvent(
      draft,
      agentId,
      'Ran replay in practice mode',
      `${getAgent(agentId).name} on historical work; nothing was sent or changed`,
    );
  });
}

export function useAutonomy(agent: AgentDef): AutonomyLevel {
  return agentsStore.useStore(
    (s) => s.autonomy[agent.id] ?? agent.defaultAutonomy,
  );
}

export function useStatus(agent: AgentDef): AgentStatus {
  return agentsStore.useStore((s) => s.status[agent.id] ?? agent.defaultStatus);
}

export interface EffectiveGuardrail {
  guardrail: Guardrail;
  state: GuardrailState;
}

export function useGuardrails(agent: AgentDef): EffectiveGuardrail[] {
  return agentsStore.useStore((s) =>
    agent.guardrails.map((guardrail) => ({
      guardrail,
      state: s.guardrails[guardrail.id] ?? defaultGuardrailState(guardrail),
    })),
  );
}
